import { AuthTokenStorageService } from '@/lib/auth';
import { DashboardStatsDTO } from '@/shared/user/dashboard';
import { SidebarStatsParams } from '@/types/sidebarStats';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  confirmContactChange,
  createUserInvoice,
  fetchDashboardStats,
  fetchSidebarStats,
  fetchUserTransaction,
  fetchUserTransactions,
  fetchTransactionSummary,
  sendContactChangeOtp,
  updatePassword,
  verifyContactChangeOtp,
  fetchAiHistory,
} from '@/features/user/api/user.api';
import { userQueryKeys } from '@/features/user/api/user.keys';
import { getAuthMeUser } from '@/features/user/api/user.cache';
import { getMutationErrorMessage } from '@/lib/api';

export { userQueryKeys } from '@/features/user/api/user.keys';

const AUTH_ME_STALE_TIME = 5 * 60 * 1000;
const EMPTY_SIDEBAR_PARAMS: SidebarStatsParams = {
  lat: 0,
  lng: 0,
  radius: 0,
};

export function useAuthMe() {
  const token = AuthTokenStorageService.getAccessToken();
  const query = useQuery({
    queryKey: userQueryKeys.me,
    queryFn: getAuthMeUser,
    enabled: Boolean(token),
    staleTime: AUTH_ME_STALE_TIME,
    retry: false,
    retryOnMount: false,
  });

  return {
    ...query,
    isAuthLoading: Boolean(token) && !query.data && !query.isError,
  };
}

export function useSidebarStats(params: SidebarStatsParams | null | undefined) {
  const token = AuthTokenStorageService.getAccessToken();

  return useQuery({
    queryKey: userQueryKeys.sidebarStats(params ?? EMPTY_SIDEBAR_PARAMS),
    queryFn: () => fetchSidebarStats(params ?? EMPTY_SIDEBAR_PARAMS),
    enabled: Boolean(token),
  });
}

export function useDashboardStats() {
  const token = AuthTokenStorageService.getAccessToken();

  return useQuery<DashboardStatsDTO>({
    queryKey: userQueryKeys.dashboardStats,
    queryFn: fetchDashboardStats,
    enabled: !!token,
  });
}

export const useSendContactChangeOtp = () => {
  return useMutation({
    mutationFn: sendContactChangeOtp,
  });
};

export const useVerifyContactChangeOtp = () => {
  return useMutation({
    mutationFn: verifyContactChangeOtp,
  });
};

export const useConfirmContactChange = () => {
  return useMutation({
    mutationFn: confirmContactChange,
  });
};

export const useCreateUserInvoice = () => {
  return useMutation({
    mutationFn: createUserInvoice,
    onError: (error: unknown) => {
      toast.error(getMutationErrorMessage(error, 'Алдаа гарлаа'));
    },
  });
};

export function useUserTransactions(page = 1, limit = 10) {
  const token = AuthTokenStorageService.getAccessToken();

  return useQuery({
    queryKey: userQueryKeys.userTransactions(page, limit),
    queryFn: () => fetchUserTransactions(page, limit),
    enabled: !!token,
  });
}

export function useAiHistory(page = 1, limit = 10) { 
  const token = AuthTokenStorageService.getAccessToken(); 
  return useQuery({
    queryKey: userQueryKeys.aiHistory(page, limit),
    queryFn: () => fetchAiHistory(page, limit),
    enabled: !!token,
  });
}

export function useUserTransaction(
  transactionId: number | null | undefined,
  options?: { enabled?: boolean }
) {
  const token = AuthTokenStorageService.getAccessToken();
  const enabled = options?.enabled ?? true;

  return useQuery({
    queryKey: userQueryKeys.userTransaction(transactionId ?? 0),
    queryFn: () => fetchUserTransaction(transactionId as number),
    enabled: !!token && !!transactionId && enabled,
  });
}

export const useUpdatePassword = () => {
  return useMutation({
    mutationFn: updatePassword,
  });
};

export function useTransactionSummary() {
  const token = AuthTokenStorageService.getAccessToken();

  return useQuery({
    queryKey: userQueryKeys.transactionSummary,
    queryFn: fetchTransactionSummary,
    enabled: !!token,
  });
}
